// Account → Billing (BL-132): the mockup «Account» Billing card, only when a Stripe customer exists. The billing name,
// address, VAT number and the card live in Stripe (ADR-0046) - edited in the customer portal; the invoices and receipts
// come from GET /api/billing/invoices with Stripe's own PDF. Since ADR-0058 Stripe issues them as the seller (merchant of
// record, number like 6E1TMRMP-0001); older subscriptions keep VALAS as the seller on their invoices.
import { useEffect, useState } from "react";
import { getInvoices, formatAmount, usePortal, type Invoice } from "../billing";
import { t, type MessageKey } from "../i18n";
import { longDay } from "./SubscribePage";

const STATUS: Record<string, MessageKey> = {
  paid: "billing.status.paid",
  open: "billing.status.open",
  void: "billing.status.void",
  uncollectible: "billing.status.uncollectible",
};

export function BillingCard() {
  const portal = usePortal();
  // undefined - loading, null - Stripe could not be reached (424) or another failure.
  const [invoices, setInvoices] = useState<Invoice[] | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    getInvoices()
      .then((r) => alive && setInvoices(Array.isArray(r) ? r : []))
      .catch(() => alive && setInvoices(null));
    return () => {
      alive = false;
    };
  }, []);

  const toPortal = (label: string, testId: string) => (
    <button type="button" className="text-btn" disabled={portal.busy} onClick={() => void portal.open()} data-testid={testId}>
      {label}
    </button>
  );
  const [failedBefore, failedAfter] = t("billing.failed").split("{link}");

  return (
    <section id="billing" className="pv-card billing-card" data-testid="billing-card">
      <div className="acct-head">
        <h2>{t("billing.title")}</h2>
        <p className="acct-note">{t("billing.lead")}</p>
      </div>
      <div className="billing-cols">
        <div className="billing-col">
          <div className="billing-col-head">
            <span className="plan-label">{t("billing.details")}</span>
            {toPortal(t("billing.edit"), "billing-edit")}
          </div>
          <p>{t("billing.detailsText")}</p>
        </div>
        <div className="billing-col">
          <div className="billing-col-head">
            <span className="plan-label">{t("billing.method")}</span>
            {toPortal(t("billing.update"), "billing-update")}
          </div>
          <p>{t("billing.methodText")}</p>
        </div>
      </div>
      {portal.error && (
        <p className="plan-error" role="alert">
          {portal.error}
        </p>
      )}
      <div className="billing-invoices">
        <span className="plan-label">{t("billing.invoices")}</span>
        {invoices === undefined && <p className="hint">{t("billing.loading")}</p>}
        {invoices === null && (
          <p className="billing-failed" data-testid="invoices-failed">
            {failedBefore}
            <button type="button" className="text-btn" disabled={portal.busy} onClick={() => void portal.open()}>
              {t("billing.failedLink")}
            </button>
            {failedAfter}
          </p>
        )}
        {invoices && invoices.length === 0 && <p className="hint">{t("billing.none")}</p>}
        {invoices && invoices.length > 0 && (
          <div className="table-scroll">
            <table className="acct-table billing-table" data-testid="invoices">
              <thead>
                <tr>
                  <th>{t("billing.colDate")}</th>
                  <th>{t("billing.colNumber")}</th>
                  <th className="billing-amount">{t("billing.colAmount")}</th>
                  <th>{t("billing.colStatus")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv, i) => (
                  <tr key={inv.number ?? i}>
                    <td className="billing-date">{longDay(inv.date)}</td>
                    <td className="billing-number">{inv.number ?? t("common.none")}</td>
                    <td className="billing-amount">{formatAmount(inv.amount, inv.currency)}</td>
                    <td className={inv.status === "open" ? "billing-open" : "billing-status"}>
                      {STATUS[inv.status] ? t(STATUS[inv.status]!) : inv.status}
                    </td>
                    <td className="billing-links">
                      {inv.status === "open" && inv.hosted_invoice_url && (
                        <a href={inv.hosted_invoice_url} target="_blank" rel="noopener">
                          {t("billing.pay")}
                        </a>
                      )}
                      {inv.invoice_pdf && (
                        <a href={inv.invoice_pdf} target="_blank" rel="noopener">
                          {t("billing.pdf")}
                        </a>
                      )}
                      {inv.receipt_url && (
                        <a href={inv.receipt_url} target="_blank" rel="noopener">
                          {t("billing.receipt")}
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
